import { Fragment, memo } from 'react'

import { GameId, StoreOfferProduct } from '@common'
import cx from 'classnames'
import Image from 'next/image'

import BrawlHyperchargeHeroBg from '@/assets/images/storefronts/brawlstars/bundle-hypercharge.png'
import CocHero1 from '@/assets/images/storefronts/clashofclans/bundle-hero.png'
import { useLocale } from '@/features/locale/useLocale'
import { useTranslatable } from '@/localization/useTranslatable'
import { useSnowplowProductCardViewImpression } from '@/utils/snowplow'

import { BrawlCardProduct } from './BrawlCardProduct'
import { CardOfferContainer } from './CardOfferContainer'
import { CardProduct } from './CardProduct'
import { brawlHyperChargeAsset, isBrawlerOrSkin, useBrawlCharacterImage } from '../../brawlstars/bundle/helpers'
import { hyperchargeIdToCharacter } from '../../brawlstars/constants'
import { ProductsContainer } from '../structure/ProductsContainer'
import { StoreTypography } from '../typography/StoreTypography'

import styles from './cardBundle.module.scss'

type BundleProps = {
  children?: React.ReactNode
  game: GameId
  bundle: StoreOfferProduct
}

function getHeroImage(game: GameId, bundle: StoreOfferProduct) {
  switch (game) {
    case 'clashroyale':
      const chronosHero = bundle.images.at(0)
      return chronosHero ?? CocHero1
    case 'clashofclans':
      const cocChronosHero = bundle.images.at(0)
      return cocChronosHero ?? CocHero1
    default:
      return CocHero1
  }
}

export const CardBundle = memo(({ game, bundle }: BundleProps) => {
  const translatable = useTranslatable()
  const locale = useLocale()
  const productCardSize =
    bundle.contents.length > 6 ? 'size-bundle-xs' : bundle.contents.length > 2 ? 'size-bundle-sm' : 'size-bundle-lg'

  const brawl = game === 'brawlstars'
  const title = translatable(bundle.title, 'title')

  // Brawl specific
  const characterIconId = brawl
    ? bundle.contents.find((content) => brawlHyperChargeAsset(content.product.id))?.product.id
    : undefined

  const characterName = characterIconId && hyperchargeIdToCharacter[characterIconId]
  const characterImage = useBrawlCharacterImage(characterIconId)

  const BrawlHero = characterIconId ? BrawlHyperchargeHeroBg : bundle.images[0]
  // end of Brawl specific

  const singleProduct = bundle.contents.length === 1

  const hero = brawl ? BrawlHero : getHeroImage(game, bundle)
  const snowplowViewImpressionRef = useSnowplowProductCardViewImpression<HTMLDivElement>(
    bundle,
    'CardBundle',
    'CardBundle',
  )

  return (
    <CardOfferContainer offerType="bundle" product={bundle} ref={snowplowViewImpressionRef}>
      <div className={cx(styles.heroContainer, styles.heroWrapper, styles[game])}>
        <Image src={hero} alt="Offer hero" fill quality={100} sizes="400px" priority />
        {characterImage && (
          <Image
            src={characterImage}
            alt={characterName ?? 'Brawl character'}
            fill
            style={{ objectFit: 'contain' }}
            sizes="200px"
            quality={100}
            className={styles.heroCharacter}
          />
        )}
      </div>
      <StoreTypography
        tag="h3"
        textSize="md"
        style="outlined"
        className={cx(styles.title, styles[game], styles[`locale-${locale}`])}
      >
        {title}
      </StoreTypography>
      <ProductsContainer narrow={bundle.contents.length === 3} game={game} singleProduct={singleProduct}>
        {bundle.contents.map((content, i) => (
          <Fragment key={i}>
            {game === 'brawlstars' ? (
              <>
                <BrawlCardProduct
                  key={content.product.id}
                  size={productCardSize}
                  game={game}
                  content={content}
                  title={translatable(content.product.title, 'title')}
                  offerContent
                  singleProduct={singleProduct}
                  isHypercharge={brawlHyperChargeAsset(content.product.id)}
                  isBrawlerOrSkin={isBrawlerOrSkin(content)}
                />
              </>
            ) : (
              <CardProduct
                key={content.product.id}
                offerType="bundle"
                size={productCardSize}
                game={game}
                content={content}
                title={translatable(content.product.title, 'title')}
                offerContent
              />
            )}
          </Fragment>
        ))}
      </ProductsContainer>
    </CardOfferContainer>
  )
})

CardBundle.displayName = 'CardBundle'
